import { formatAmount } from "../../../../utils/hooks/dataHandler";

const toNumber = (value) => {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number : 0;
};

const toCents = (value) => Math.round((toNumber(value) + Number.EPSILON) * 100);
const fromCents = (value) => value / 100;
const roundWhole = (value) => Math.round(toNumber(value) + Number.EPSILON);

export const QUALIFYING_COLUMN_FIELDS = [
  "amountI",
  "amountII",
  "amountIII",
  "amountIV",
];

export const QUALIFYING_FIELDS_BY_ARRANGEMENT = {
  companyTreasury: ["amountI"],
  holdingTreasury: ["amountI", "amountIII"],
  spvOpenMarket: ["amountI", "amountII"],
  newCompanyShares: [],
  newHoldingShares: ["amountI", "amountIV"],
};

export const formatSharedBasedPaymentCost = (value) =>
  value === "" || value === null || value === undefined
    ? ""
    : formatAmount(value, false, false, false, true);

export const getTrackerWorkingText = (working, method, transactions = []) => {
  if (!working) return "-";
  if (working.issue) return working.issue;
  const unitCost = formatSharedBasedPaymentCost(working.averageCostPerShare);

  if (working.kind === "acquisition") {
    if (method === "annualWeightedAverage") {
      return `Current YA average: ${unitCost} per share`;
    }
    if (method === "transactionWeightedAverage") {
      return `Average cost per share after row: ${unitCost}`;
    }
    return `${formatAmount(working.shares)} shares at ${unitCost} per share`;
  }

  if (
    method === "annualWeightedAverage" ||
    method === "transactionWeightedAverage"
  ) {
    const label =
      method === "annualWeightedAverage"
        ? "current YA average"
        : "average cost per share";
    return `${formatAmount(working.shares)} x ${unitCost} ${label} = ${formatSharedBasedPaymentCost(working.releasedCost)}`;
  }

  return (
    (working.layerBreakdown || [])
      .map(
        (layer) =>
          `${formatAmount(layer.shares)} from ${transactions.find((row) => row.transactionId === layer.sourceTransactionId)?.serialNo || layer.sourceTransactionId} = ${formatSharedBasedPaymentCost(layer.cost)}`
      )
      .join("\n") || "No shares released"
  );
};

// Present the existing FIFO allocations by acquisition and outflow without
// running a second allocation that could disagree with Column I.
export const getFifoShareUsage = (transactions = [], summary = {}) => {
  const outflows = transactions.filter(
    (row) =>
      (row.type === "eebrTransfer" || row.type === "otherOutflow") &&
      Number(row.shares) < 0
  );
  const usedBySourceAndOutflow = {};
  const usedByOutflow = {};

  outflows.forEach((outflow) => {
    const working = summary.transactionWorkings?.[outflow.transactionId];
    (working?.layerBreakdown || []).forEach(
      ({ sourceTransactionId, shares }) => {
        usedByOutflow[outflow.transactionId] =
          (usedByOutflow[outflow.transactionId] || 0) + shares;
        if (!usedBySourceAndOutflow[sourceTransactionId]) {
          usedBySourceAndOutflow[sourceTransactionId] = {};
        }
        usedBySourceAndOutflow[sourceTransactionId][outflow.transactionId] =
          (usedBySourceAndOutflow[sourceTransactionId][outflow.transactionId] ||
            0) + shares;
      }
    );
  });

  return {
    outflows,
    usedByOutflow,
    usedBySourceAndOutflow,
    closingSharesBySource: Object.fromEntries(
      (summary.fifoLayers || []).map((layer) => [
        layer.sourceTransactionId,
        layer.shares,
      ])
    ),
  };
};

const amountDecimalLimits = {
  amountI: 0,
  amountII: 0,
  amountIII: 0,
  amountIV: 0,
};

const parseImportedAmount = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return null;
  }
  const input = String(value).trim();
  if (!/^(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d+)?$/.test(input)) {
    return null;
  }
  const text = input.replace(/,/g, "");
  const number = Number(text);
  return Number.isFinite(number) ? { number, text } : null;
};

export const sanitizeNonNegativeAmount = (value, decimalLimit) => {
  const parsed = parseImportedAmount(value);
  if (!parsed) return "";
  const decimalPlaces = parsed.text.includes(".")
    ? parsed.text.split(".")[1].length
    : 0;
  return decimalPlaces <= decimalLimit ? parsed.number : "";
};

// Excel imports use the same applicability and precision rules as the
// deduction calculation. Invalid or inapplicable cells remain empty.
export const sanitizeQualifyingColumnValue = (field, value, arrangement) => {
  if (!(QUALIFYING_FIELDS_BY_ARRANGEMENT[arrangement] || []).includes(field)) {
    return "";
  }

  return sanitizeNonNegativeAmount(value, amountDecimalLimits[field]);
};

const sortTransactions = (transactions = []) =>
  [...transactions].sort(
    (a, b) =>
      String(a.date || "").localeCompare(String(b.date || "")) ||
      toNumber(a.sequence) - toNumber(b.sequence)
  );

const transactionCostInCents = (transaction) =>
  toCents(transaction.shareCost) +
  toCents(transaction.directAcquisitionCharges);

const trackerIssue = (current, next) => current || next;

const calculateFifoPool = (pool, transactions) => {
  const layers = [];
  const transactionWorkings = {};
  let issue = "";
  let hasShareShortfall = false;
  const shortfallTransactionIds = [];

  if (toNumber(pool.openingShares) > 0) {
    const shares = toNumber(pool.openingShares);
    const cost = toCents(pool.openingCost);
    layers.push({
      sourceTransactionId: "opening",
      shares,
      cost,
      unitCost: Math.round(cost / shares),
    });
  }

  for (const transaction of sortTransactions(transactions)) {
    const shares = toNumber(transaction.shares);
    if (transaction.type === "acquisition" || transaction.type === "opening") {
      const cost =
        transaction.type === "opening"
          ? toCents(transaction.shareCost)
          : transactionCostInCents(transaction);
      const unitCost = shares ? Math.round(cost / shares) : 0;
      if (shares > 0) {
        layers.push({
          sourceTransactionId: transaction.transactionId,
          shares,
          cost,
          unitCost,
        });
      } else if (cost) {
        issue = trackerIssue(
          issue,
          "Enter the share quantity for each acquisition with a cost."
        );
      }
      transactionWorkings[transaction.transactionId] = {
        kind: "acquisition",
        shares,
        averageCostPerShare: fromCents(unitCost),
      };
      continue;
    }

    const quantity = Math.abs(shares);
    let remaining = quantity;
    let releasedCost = 0;
    const layerBreakdown = [];
    while (remaining > 0 && layers.length) {
      const layer = layers[0];
      const releasedShares = Math.min(remaining, layer.shares);
      const cost = releasedShares * layer.unitCost;
      releasedCost += cost;
      layerBreakdown.push({
        sourceTransactionId: layer.sourceTransactionId,
        shares: releasedShares,
        cost: fromCents(cost),
      });
      layer.shares -= releasedShares;
      layer.cost -= cost;
      remaining -= releasedShares;
      if (!layer.shares) layers.shift();
    }
    if (remaining > 0) {
      hasShareShortfall = true;
      shortfallTransactionIds.push(transaction.transactionId);
      issue = trackerIssue(
        issue,
        "Review transfers: shares out exceed the available balance."
      );
    }
    transactionWorkings[transaction.transactionId] = {
      kind: "release",
      shares: quantity,
      releasedCost: fromCents(releasedCost),
      averageCostPerShare: quantity
        ? fromCents(Math.round(releasedCost / quantity))
        : 0,
      linkedClaimLineId:
        transaction.type === "eebrTransfer"
          ? transaction.linkedClaimLineId
          : "",
      layerBreakdown,
    };
  }

  return {
    transactionWorkings,
    closingShares: layers.reduce((sum, layer) => sum + layer.shares, 0),
    closingCost: fromCents(layers.reduce((sum, layer) => sum + layer.cost, 0)),
    currentYaShares: 0,
    currentYaCost: 0,
    annualAverageCostPerShare: 0,
    fifoLayers: layers.map((layer) => ({
      sourceTransactionId: layer.sourceTransactionId,
      shares: layer.shares,
      cost: fromCents(layer.cost),
    })),
    hasShareShortfall,
    shortfallTransactionIds,
    issue,
  };
};

const calculateTransactionWeightedAveragePool = (pool, transactions) => {
  let shares = toNumber(pool.openingShares);
  let cost = toCents(pool.openingCost);
  let issue = "";
  let hasShareShortfall = false;
  const shortfallTransactionIds = [];
  const transactionWorkings = {};

  for (const transaction of sortTransactions(transactions)) {
    const quantity = toNumber(transaction.shares);
    if (transaction.type === "acquisition" || transaction.type === "opening") {
      const acquiredCost =
        transaction.type === "opening"
          ? toCents(transaction.shareCost)
          : transactionCostInCents(transaction);
      shares += quantity;
      cost += acquiredCost;
      const unitCost = shares ? Math.round(cost / shares) : 0;
      transactionWorkings[transaction.transactionId] = {
        kind: "acquisition",
        shares: quantity,
        averageCostPerShare: fromCents(unitCost),
      };
      continue;
    }

    const releasedShares = Math.abs(quantity);
    if (releasedShares > shares) {
      hasShareShortfall = true;
      shortfallTransactionIds.push(transaction.transactionId);
      issue = trackerIssue(
        issue,
        "Review transfers: shares out exceed the available balance."
      );
      transactionWorkings[transaction.transactionId] = {
        kind: "release",
        shares: releasedShares,
        releasedCost: 0,
        averageCostPerShare: 0,
        linkedClaimLineId:
          transaction.type === "eebrTransfer"
            ? transaction.linkedClaimLineId
            : "",
        layerBreakdown: [],
      };
      continue;
    }

    const unitCost = shares ? Math.round(cost / shares) : 0;
    const releasedCost = releasedShares * unitCost;
    shares -= releasedShares;
    cost -= releasedCost;
    transactionWorkings[transaction.transactionId] = {
      kind: "release",
      shares: releasedShares,
      releasedCost: fromCents(releasedCost),
      averageCostPerShare: fromCents(unitCost),
      linkedClaimLineId:
        transaction.type === "eebrTransfer"
          ? transaction.linkedClaimLineId
          : "",
      layerBreakdown: [
        {
          sourceTransactionId: "weighted-average-pool",
          shares: releasedShares,
          cost: fromCents(releasedCost),
        },
      ],
    };
  }

  return {
    transactionWorkings,
    closingShares: shares,
    closingCost: shares ? fromCents(cost) : 0,
    currentYaShares: 0,
    currentYaCost: 0,
    annualAverageCostPerShare: 0,
    fifoLayers: [],
    hasShareShortfall,
    shortfallTransactionIds,
    issue,
  };
};

const calculateAnnualWeightedAveragePool = (pool, transactions) => {
  const rows = sortTransactions(transactions);
  const acquisitions = rows.filter((row) => row.type === "acquisition");
  const currentYaShares =
    toNumber(pool.openingShares) +
    acquisitions.reduce((sum, row) => sum + toNumber(row.shares), 0);
  const currentYaCostInCents =
    toCents(pool.openingCost) +
    acquisitions.reduce((sum, row) => sum + transactionCostInCents(row), 0);
  const unitCost = currentYaShares
    ? Math.round(currentYaCostInCents / currentYaShares)
    : 0;
  const transactionWorkings = {};
  let physicalShares = toNumber(pool.openingShares);
  let outflowShares = 0;
  let issue = "";
  let hasShareShortfall = false;
  const shortfallTransactionIds = [];

  for (const transaction of rows) {
    const quantity = toNumber(transaction.shares);
    if (transaction.type === "acquisition") {
      physicalShares += quantity;
      transactionWorkings[transaction.transactionId] = {
        kind: "acquisition",
        shares: quantity,
        averageCostPerShare: fromCents(unitCost),
      };
      continue;
    }

    const releasedShares = Math.abs(quantity);
    if (releasedShares > physicalShares) {
      hasShareShortfall = true;
      shortfallTransactionIds.push(transaction.transactionId);
      issue = trackerIssue(
        issue,
        "Review transfers: shares out exceed the available balance."
      );
    }
    physicalShares -= releasedShares;
    outflowShares += releasedShares;
    const releasedCost = releasedShares * unitCost;
    transactionWorkings[transaction.transactionId] = {
      kind: "release",
      shares: releasedShares,
      releasedCost: fromCents(releasedCost),
      averageCostPerShare: fromCents(unitCost),
      linkedClaimLineId:
        transaction.type === "eebrTransfer"
          ? transaction.linkedClaimLineId
          : "",
      layerBreakdown: [
        {
          sourceTransactionId: "annual-weighted-average",
          shares: releasedShares,
          cost: fromCents(releasedCost),
        },
      ],
    };
  }

  const closingShares = currentYaShares - outflowShares;
  const closingCost = closingShares
    ? fromCents(currentYaCostInCents - outflowShares * unitCost)
    : 0;
  return {
    transactionWorkings,
    closingShares,
    closingCost,
    currentYaShares,
    currentYaCost: fromCents(currentYaCostInCents),
    annualAverageCostPerShare: fromCents(unitCost),
    fifoLayers: [],
    hasShareShortfall,
    shortfallTransactionIds,
    issue,
  };
};

export const calculateTrackerPool = (pool, transactions = []) => {
  if (!pool) {
    return {
      transactionWorkings: {},
      fifoLayers: [],
      hasShareShortfall: false,
      shortfallTransactionIds: [],
      issue: "Share pool is not configured.",
    };
  }
  const poolTransactions = transactions.filter(
    (transaction) => transaction.poolId === pool.poolId
  );
  const hasInvalidValue =
    [pool.openingShares, pool.openingCost].some(
      (value) => !Number.isFinite(Number(value || 0)) || Number(value || 0) < 0
    ) ||
    poolTransactions.some((row) => {
      const shares = Number(row.shares || 0);
      const isOutflow =
        row.type === "eebrTransfer" || row.type === "otherOutflow";
      return (
        !Number.isFinite(shares) ||
        (isOutflow ? shares > 0 : shares < 0) ||
        [row.shareCost, row.directAcquisitionCharges].some(
          (value) =>
            !Number.isFinite(Number(value || 0)) || Number(value || 0) < 0
        )
      );
    });
  if (hasInvalidValue) {
    return {
      transactionWorkings: {},
      fifoLayers: [],
      hasShareShortfall: false,
      shortfallTransactionIds: [],
      issue:
        "Check the tracker quantities and costs before reviewing the balance.",
    };
  }

  const result =
    pool.method === "annualWeightedAverage"
      ? calculateAnnualWeightedAveragePool(pool, poolTransactions)
      : pool.method === "transactionWeightedAverage"
        ? calculateTransactionWeightedAveragePool(pool, poolTransactions)
        : calculateFifoPool(pool, poolTransactions);
  if (poolTransactions.some((row) => toNumber(row.shares) !== 0 && !row.date)) {
    result.issue = trackerIssue(
      result.issue,
      "Add transaction dates to complete the closing balance."
    );
  }
  return result;
};

const requiresRechargeDate = (arrangement) =>
  ["holdingTreasury", "spvOpenMarket", "newHoldingShares"].includes(
    arrangement
  );

const calculateDeductionDate = (line) => {
  if (!line.exerciseOrVestingDate) return "";
  if (!requiresRechargeDate(line.arrangement)) {
    return line.exerciseOrVestingDate;
  }
  if (!line.rechargeOrLiabilityDate) return "";
  return line.rechargeOrLiabilityDate > line.exerciseOrVestingDate
    ? line.rechargeOrLiabilityDate
    : line.exerciseOrVestingDate;
};

const grossQualifyingCost = (line, amountI) => {
  switch (line.arrangement) {
    case "companyTreasury":
      return amountI;
    case "holdingTreasury":
      return Math.min(amountI, toNumber(line.amountIII));
    case "spvOpenMarket":
      return Math.min(amountI, toNumber(line.amountII));
    case "newHoldingShares":
      return Math.min(amountI, toNumber(line.amountIV));
    case "newCompanyShares":
    default:
      return 0;
  }
};

const reasonMessages = {
  MISSING_EXERCISE_OR_VESTING_DATE:
    "Enter the exercise or vesting date to determine the deduction period.",
  MISSING_RECHARGE_OR_LIABILITY_DATE:
    "Enter the recharge or liability date for this arrangement.",
  OWN_NEW_SHARES_DISALLOWED:
    "Newly issued shares of the company do not qualify for this deduction.",
  NO_COMPANY_PAYMENT:
    "No company payment is available in Column I for this arrangement.",
  OUTSIDE_SELECTED_YA:
    "The deduction date falls outside the selected year of assessment.",
};

const createLineStatus = (selectedYaStatus, reasonCodes) => {
  const explanations = reasonCodes
    .map((reasonCode) => reasonMessages[reasonCode])
    .filter(Boolean);

  if (selectedYaStatus === "Eligible - selected YA") {
    return {
      code: "eligible",
      label: "Eligible",
      tone: "success",
      explanation:
        "The deduction date falls within the selected year of assessment.",
    };
  }

  if (selectedYaStatus === "Outside selected YA") {
    return {
      code: "outsideYa",
      label: "Outside YA",
      tone: "warning",
      explanation: explanations.join(" "),
    };
  }

  const needsInput = reasonCodes.some((reasonCode) =>
    [
      "MISSING_EXERCISE_OR_VESTING_DATE",
      "MISSING_RECHARGE_OR_LIABILITY_DATE",
      "NO_COMPANY_PAYMENT",
    ].includes(reasonCode)
  );

  return {
    code: "disallowed",
    label: "Disallowed",
    tone: needsInput ? "warning" : "error",
    explanation:
      explanations.join(" ") ||
      "No deductible amount remains after applying the relevant limits and employee payment.",
  };
};

const arrangementColumn = {
  companyTreasury: "acquired",
  holdingTreasury: "acquired",
  spvOpenMarket: "acquired",
  newCompanyShares: "newlyIssued",
  newHoldingShares: "newlyIssued",
};

const buildFormulaTrace = (line, columnIAmount, gross) => {
  switch (line.arrangement) {
    case "companyTreasury":
      return `I (${columnIAmount})`;
    case "holdingTreasury":
      return `min(I ${columnIAmount}, III ${toNumber(line.amountIII)})`;
    case "spvOpenMarket":
      return `min(I ${columnIAmount}, II ${toNumber(line.amountII)})`;
    case "newCompanyShares":
      return "Nil - newly issued company shares";
    case "newHoldingShares":
      return `min(I ${columnIAmount}, IV ${toNumber(line.amountIV)})`;
    default:
      return `Unsupported arrangement; gross ${gross}`;
  }
};

const isWholeNumber = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return false;
  }
  const number = Number(value);
  return Number.isFinite(number) && Number.isInteger(number);
};

const decimalPlaces = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return 0;
  }
  const text = String(value).trim().replace(/,/g, "").toLowerCase();
  if (!Number.isFinite(Number(text))) return 0;
  const [coefficient, exponentText] = text.split("e");
  const fractionLength = (coefficient.split(".")[1] || "").length;
  const exponent = Number(exponentText || 0);
  return Math.max(0, fractionLength - exponent);
};

const createValidationIssue = (scope, code, message) => ({
  scope,
  code,
  message,
});

export const validateSharedBasedPayment = (schedule = {}, calculation = {}) => {
  const errors = [];
  const warnings = [];
  const transactions = Array.isArray(schedule.transactions)
    ? schedule.transactions
    : [];
  const claimLines = Array.isArray(schedule.claimLines)
    ? schedule.claimLines
    : [];

  if (Number(schedule.ya) < 2026) {
    errors.push(
      createValidationIssue(
        "schedule",
        "INVALID_YA",
        "Selected YA must be YA 2026 or later."
      )
    );
  }

  if (
    !schedule.basisPeriodStart ||
    !schedule.basisPeriodEnd ||
    schedule.basisPeriodStart > schedule.basisPeriodEnd
  ) {
    errors.push(
      createValidationIssue(
        "schedule",
        "INVALID_BASIS_PERIOD",
        "Basis period start and end are required."
      )
    );
  }

  transactions.forEach((transaction) => {
    const scope = transaction.transactionId || "tracker";
    const isOutflow =
      transaction.type === "eebrTransfer" ||
      transaction.type === "otherOutflow";
    if (
      !isWholeNumber(transaction.shares) ||
      (isOutflow
        ? Number(transaction.shares) >= 0
        : Number(transaction.shares) < 0)
    ) {
      errors.push(
        createValidationIssue(
          scope,
          "NON_WHOLE_VALUE",
          isOutflow
            ? "EEBR transfers and other outflows require negative whole-number shares."
            : "Acquisition shares must be non-negative whole numbers."
        )
      );
    }
    ["shareCost", "directAcquisitionCharges"].forEach((field) => {
      if (
        transaction[field] !== undefined &&
        decimalPlaces(transaction[field]) > 2
      ) {
        errors.push(
          createValidationIssue(
            scope,
            "COST_DECIMALS",
            "Share-cost amounts allow at most 2 decimals."
          )
        );
      }
    });
    if (transaction.type === "eebrTransfer" && !transaction.linkedClaimLineId) {
      errors.push(
        createValidationIssue(
          scope,
          "UNLINKED_TRANSFER",
          "Tracked EEBR transfers must link to a deduction line."
        )
      );
    }
  });

  Object.values(calculation.inventory?.pools || {}).forEach((pool) => {
    (pool.shortfallTransactionIds || []).forEach((transactionId) => {
      errors.push(
        createValidationIssue(
          transactionId || pool.poolId,
          "SHARE_SHORTFALL",
          "Share outflow exceeds available balance."
        )
      );
    });
  });

  claimLines.forEach((line) => {
    const scope = line.claimLineId || "deduction-line";
    if (!line.exerciseOrVestingDate) {
      errors.push(
        createValidationIssue(
          scope,
          "MISSING_EXERCISE_OR_VESTING_DATE",
          "Exercise/vesting date is required."
        )
      );
    }
    if (
      requiresRechargeDate(line.arrangement) &&
      !line.rechargeOrLiabilityDate
    ) {
      errors.push(
        createValidationIssue(
          scope,
          "MISSING_RECHARGE_OR_LIABILITY_DATE",
          "Recharge/liability date is required for this arrangement."
        )
      );
    }

    if (!isWholeNumber(line.employeePayment)) {
      errors.push(
        createValidationIssue(
          scope,
          "NON_WHOLE_VALUE",
          "Employee payment must be a whole number."
        )
      );
    }

    const applicableFields =
      QUALIFYING_FIELDS_BY_ARRANGEMENT[line.arrangement] || [];
    if (
      applicableFields.includes("amountIV") &&
      !isWholeNumber(line.amountIV)
    ) {
      errors.push(
        createValidationIssue(
          scope,
          "NON_WHOLE_VALUE",
          "Column IV must be a whole number."
        )
      );
    }
    ["amountI", "amountII", "amountIII"]
      .filter((field) => applicableFields.includes(field))
      .forEach((field) => {
        if (
          line[field] !== undefined &&
          line[field] !== null &&
          line[field] !== "" &&
          !isWholeNumber(line[field])
        ) {
          errors.push(
            createValidationIssue(
              scope,
              "NON_WHOLE_VALUE",
              `Column ${field.replace("amount", "")} must be a whole number.`
            )
          );
        }
      });
  });

  if (schedule.useCostTracker === false) {
    warnings.push(
      createValidationIssue(
        "schedule",
        "MANUAL_COST_VALUES",
        "Cost tracker is off. Manual amount I/II/III values should be reviewed against supporting records."
      )
    );
  }

  return {
    errors,
    warnings,
    status: errors.length ? "blocked" : warnings.length ? "warning" : "valid",
  };
};

export const buildFurtherDeductionOutput = (
  schedule = {},
  calculation = {},
  validation = { status: "blocked" }
) => {
  const baseOutput = {
    scheduleId: schedule.scheduleId || "",
    ya: Number(schedule.ya),
    basisPeriod: {
      start: schedule.basisPeriodStart || "",
      end: schedule.basisPeriodEnd || "",
    },
    mainComputationCategory: "further_deduction",
    mainComputationLabel: "Share-based payment - qualifying deduction",
    lineResults: calculation.lineResults || [],
  };

  if (validation.status === "blocked") {
    return {
      ...baseOutput,
      qualifyingDeductionSgd: 0,
      status: "blocked",
      rolloverPayload: null,
    };
  }

  return {
    ...baseOutput,
    basisPeriod: calculation.basisPeriod || baseOutput.basisPeriod,
    qualifyingDeductionSgd: calculation.qualifyingDeductionSgd || 0,
    mainComputationCategory:
      calculation.mainComputationCategory || baseOutput.mainComputationCategory,
    mainComputationLabel:
      calculation.mainComputationLabel || baseOutput.mainComputationLabel,
    status: validation.status,
    rolloverPayload: calculation.rolloverPayload || null,
  };
};

export const calculateSharedBasedPayment = (schedule = {}) => {
  const costPools = Array.isArray(schedule.costPools)
    ? schedule.costPools.slice(0, 1)
    : [];
  const transactions = Array.isArray(schedule.transactions)
    ? schedule.transactions
    : [];
  const claimLines = Array.isArray(schedule.claimLines)
    ? schedule.claimLines
    : [];
  const inventoryTrackerResults = costPools.map((pool) => ({
    pool,
    summary: calculateTrackerPool(pool, transactions),
  }));
  const trackerResults = schedule.useCostTracker ? inventoryTrackerResults : [];
  const inventoryClaimCostCents = {};
  inventoryTrackerResults.forEach(({ summary }) => {
    Object.values(summary.transactionWorkings || {}).forEach((working) => {
      if (!working.linkedClaimLineId) return;
      inventoryClaimCostCents[working.linkedClaimLineId] =
        (inventoryClaimCostCents[working.linkedClaimLineId] || 0) +
        toCents(working.releasedCost);
    });
  });
  const claimCostCents = schedule.useCostTracker ? inventoryClaimCostCents : {};

  let specialAccount = toNumber(
    costPools.find((pool) => pool.acquiredShareCategory === "companyTreasury")
      ?.openingSpecialAccount
  );
  const lineResults = [...claimLines]
    .sort(
      (a, b) =>
        String(a.exerciseOrVestingDate || "").localeCompare(
          String(b.exerciseOrVestingDate || "")
        ) || String(a.claimLineId).localeCompare(String(b.claimLineId))
    )
    .map((line) => {
      const trackedCost = fromCents(claimCostCents[line.claimLineId] || 0);
      const columnIAmount =
        schedule.useCostTracker && trackedCost > 0
          ? roundWhole(trackedCost)
          : toNumber(line.amountI);
      const columnAmounts = {
        amountI: columnIAmount,
        amountII: toNumber(line.amountII),
        amountIII: toNumber(line.amountIII),
        amountIV: toNumber(line.amountIV),
      };
      const gross = grossQualifyingCost(line, columnIAmount);
      const deductionDate = calculateDeductionDate(line);
      const reasonCodes = [];
      if (!line.exerciseOrVestingDate) {
        reasonCodes.push("MISSING_EXERCISE_OR_VESTING_DATE");
      } else if (
        requiresRechargeDate(line.arrangement) &&
        !line.rechargeOrLiabilityDate
      ) {
        reasonCodes.push("MISSING_RECHARGE_OR_LIABILITY_DATE");
      }
      if (line.arrangement === "newCompanyShares") {
        reasonCodes.push("OWN_NEW_SHARES_DISALLOWED");
      }
      if (
        ["holdingTreasury", "newHoldingShares", "spvOpenMarket"].includes(
          line.arrangement
        ) &&
        columnIAmount === 0
      ) {
        reasonCodes.push("NO_COMPANY_PAYMENT");
      }
      if (
        deductionDate &&
        (!schedule.basisPeriodStart ||
          !schedule.basisPeriodEnd ||
          deductionDate < schedule.basisPeriodStart ||
          deductionDate > schedule.basisPeriodEnd)
      ) {
        reasonCodes.push("OUTSIDE_SELECTED_YA");
      }

      const specialAccountOpening = specialAccount;
      let deduction = Math.max(0, gross - toNumber(line.employeePayment));
      let specialAccountAddition = 0;
      let specialAccountUsed = 0;
      if (!reasonCodes.length && line.arrangement === "companyTreasury") {
        const preSpecial = gross - toNumber(line.employeePayment);
        if (preSpecial < 0) {
          specialAccountAddition = Math.abs(preSpecial);
          specialAccount += specialAccountAddition;
          deduction = 0;
        } else {
          specialAccountUsed = Math.min(specialAccount, preSpecial);
          specialAccount -= specialAccountUsed;
          deduction = preSpecial - specialAccountUsed;
        }
      }
      const roundedDeduction = roundWhole(deduction);
      const selectedYaStatus = reasonCodes.includes("OUTSIDE_SELECTED_YA")
        ? "Outside selected YA"
        : reasonCodes.length || roundedDeduction === 0
          ? "Disallowed"
          : "Eligible - selected YA";
      const status = createLineStatus(selectedYaStatus, reasonCodes);

      return {
        claimLineId: line.claimLineId,
        arrangement: line.arrangement,
        column: arrangementColumn[line.arrangement] || line.column,
        employeePayment: toNumber(line.employeePayment),
        trackedCost,
        trackerLinked: transactions.some(
          (row) =>
            row.type === "eebrTransfer" &&
            row.linkedClaimLineId === line.claimLineId
        ),
        columnIAmount,
        columnAmounts,
        grossQualifyingCost: gross,
        deductionDate,
        selectedYaStatus,
        status,
        roundedDeduction:
          selectedYaStatus === "Eligible - selected YA" ? roundedDeduction : 0,
        reasonCodes,
        specialAccountOpening,
        specialAccountAddition,
        specialAccountUsed,
        specialAccountClosing: specialAccount,
        formulaTrace: buildFormulaTrace(line, columnIAmount, gross),
      };
    });
  const lineResultsById = Object.fromEntries(
    lineResults.map((result) => [result.claimLineId, result])
  );

  const claimCosts = Object.fromEntries(
    Object.entries(claimCostCents).map(([claimLineId, value]) => [
      claimLineId,
      fromCents(value),
    ])
  );
  const inventory = {
    pools: Object.fromEntries(
      inventoryTrackerResults.map(({ pool, summary }) => [
        pool.poolId,
        {
          poolId: pool.poolId,
          method: pool.method,
          closingShares: summary.closingShares || 0,
          closingCost: summary.closingCost || 0,
          fifoLayers: summary.fifoLayers || [],
          transactionWorkings: summary.transactionWorkings || {},
          shortfallTransactionIds: summary.shortfallTransactionIds || [],
          warnings: (summary.shortfallTransactionIds || []).map(
            (transactionId) => ({
              transactionId,
              code: "SHARE_SHORTFALL",
            })
          ),
          methodSummary: {
            currentYaShares: summary.currentYaShares || 0,
            currentYaCost: summary.currentYaCost || 0,
            annualAverageCostPerShare: summary.annualAverageCostPerShare || 0,
          },
        },
      ])
    ),
    claimCosts: Object.fromEntries(
      Object.entries(inventoryClaimCostCents).map(([claimLineId, value]) => [
        claimLineId,
        fromCents(value),
      ])
    ),
  };
  const qualifyingDeductionSgd = lineResults.reduce(
    (sum, result) => sum + result.roundedDeduction,
    0
  );
  const calculation = {
    scheduleId: schedule.scheduleId || "",
    ya: schedule.ya,
    basisPeriod: {
      start: schedule.basisPeriodStart || "",
      end: schedule.basisPeriodEnd || "",
    },
    qualifyingDeductionSgd,
    mainComputationCategory: "further_deduction",
    mainComputationLabel: "Share-based payment - qualifying deduction",
    status: qualifyingDeductionSgd > 0 ? "valid" : "warning",
    trackerResults,
    lineResults,
    lineResultsById,
    claimCosts,
    inventory,
    rolloverPayload: {
      costPools: Object.values(inventory.pools).map((pool) => ({
        poolId: pool.poolId,
        method: pool.method,
        closingShares: pool.closingShares,
        closingCost: pool.closingCost,
        fifoLayers: pool.fifoLayers,
      })),
      closingSpecialAccount: specialAccount,
    },
  };
  const validation = validateSharedBasedPayment(schedule, calculation);
  const output = buildFurtherDeductionOutput(schedule, calculation, validation);

  // Keep the existing flat result for current table/metric consumers while
  // exposing the prototype-compatible calculation/validation/output contract.
  return { ...calculation, calculation, validation, output };
};
